// covered-by: frontends/carrie/src/utils/__tests__/memoryCitationFocus.test.ts

/**
 * The link between a memory's chip, the sentence it supports and its rail card.
 *
 * Hovering or focusing a chip tints its sentence, and hovering or focusing its
 * rail card does the same. The thread and the rail are separate trees with no
 * shared parent on every page that draws them (the legacy chat has no rail at
 * all), so the state lives in this small external store instead of a context a
 * page would have to mount.
 *
 * why keyed by `scope::memoryId`: a memory can support two answers of one thread,
 * and a card must light only the sentence of the answer it was drawn for.
 */

import {
  type FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

export const memoryFocusKey = (scope: string, memoryId: string): string =>
  `${scope}::${memoryId}`;

type Listener = () => void;

/** Who holds a key lit: a chip or card under the pointer or focused. Counted so two holders do not clear each other. */
const holders = new Map<string, number>();
/** Readers per key: a change to one key wakes its own readers, not every chip, sentence and card on the page. */
const listeners = new Map<string, Set<Listener>>();

function notify(...keys: string[]): void {
  new Set(keys).forEach((key) => {
    listeners.get(key)?.forEach((listener) => listener());
  });
}

function subscribeTo(key: string, listener: Listener): () => void {
  const set = listeners.get(key) ?? new Set<Listener>();
  set.add(listener);
  listeners.set(key, set);
  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(key);
  };
}

export function acquireLit(key: string): void {
  holders.set(key, (holders.get(key) ?? 0) + 1);
  notify(key);
}

export function releaseLit(key: string): void {
  const next = (holders.get(key) ?? 0) - 1;
  if (next > 0) holders.set(key, next);
  else holders.delete(key);
  notify(key);
}

export function isLit(key: string): boolean {
  return (holders.get(key) ?? 0) > 0;
}

/** For tests: forget everything, so one case cannot light the next. */
export function resetMemoryFocus(): void {
  const keys = Array.from(listeners.keys());
  holders.clear();
  notify(...keys);
}

/** For tests: how many keys the store still remembers. A page of answers must not grow it without end. */
export function memoryFocusBookkeeping(): {
  holders: number;
  listeners: number;
} {
  return { holders: holders.size, listeners: listeners.size };
}

export interface MemoryFocusState {
  lit: boolean;
}

/** One key's lit flag, so a reader re-renders only when its own key changes. */
export function useMemoryFocus(key: string): MemoryFocusState {
  const subscribe = useCallback(
    (listener: Listener) => subscribeTo(key, listener),
    [key],
  );
  const read = useCallback(() => isLit(key), [key]);
  const lit = useSyncExternalStore(subscribe, read, read);
  return useMemo(() => ({ lit }), [lit]);
}

/**
 * True when the browser would draw a focus ring on what took focus: a keyboard
 * arrival, not a mouse click. An engine with no `:focus-visible` counts as true,
 * which is the old behaviour.
 */
function takesKeyboardFocus(target: EventTarget): boolean {
  if (!(target instanceof Element)) return true;
  try {
    return target.matches(":focus-visible");
  } catch {
    return true;
  }
}

/**
 * Handlers that hold `key` lit while the pointer is over, or keyboard focus is
 * inside, the element they are spread on.
 *
 * why a ref per source: pointer and focus are separate holders. A reader who
 * tabs to a chip and then moves the mouse off it must not clear the focus.
 *
 * why focus only when it is visible: a mouse click focuses the chip too, and a
 * hold taken then kept the sentence tinted long after the pointer had left. The
 * tint while its card is open is held by the card (`MemoryCitationChip`).
 */
export function useMemoryLitHandlers(key: string) {
  const held = useRef({ pointer: false, focus: false });
  const set = useCallback(
    (source: "pointer" | "focus", on: boolean) => {
      if (held.current[source] === on) return;
      held.current[source] = on;
      if (on) acquireLit(key);
      else releaseLit(key);
    },
    [key],
  );
  useEffect(() => {
    const current = held.current;
    return () => {
      // why: a chip that unmounts while hovered must not leave its sentence lit.
      if (current.pointer) releaseLit(key);
      if (current.focus) releaseLit(key);
      current.pointer = false;
      current.focus = false;
    };
  }, [key]);
  return {
    onPointerEnter: () => set("pointer", true),
    onPointerLeave: () => set("pointer", false),
    onFocus: (event: FocusEvent<HTMLElement>) => {
      if (takesKeyboardFocus(event.target)) set("focus", true);
    },
    onBlur: () => set("focus", false),
  };
}
